import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import useDebounce from '@hooks/useDebounce';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { SkeletonLoader } from '@widget/loaders';
import Input from '../Input';
import MediaPickCard from './MediaPickCard';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import useInfiniteScroll from '@hooks/useInfiniteScroll';
const IMAGES_PER_PAGE = 30; // Unsplash's ceiling — it clamps anything higher
// The proxy's editorial (no-query) feed doesn't page reliably — seed the
// default view with a real query so it paginates exactly like a search.
// The search input itself stays empty.
const DEFAULT_QUERY = 'wallpapers';
// Session-scoped search cache — repeat queries (and reopening the modal)
// render instantly instead of refetching. Module singleton, shared by every
// picker instance; entries hold the accumulated pages for a query.
const searchCache = new Map();
/**
 * @export
 * Keyword search of the platform image library (Unsplash, proxied by the Lucy
 * `SearchImagesFromUnsplash` service), with infinite scroll.
 */
const GalleryView = (props) => {
    var _a;
    const { show, searchParameters, onPick, isSelected, onToggle, activeValue, grid, onQueryChange, hideSearch } = props;
    const context = useUXPContext();
    const [ownQuery, setOwnQuery] = React.useState('');
    const query = (_a = props.query) !== null && _a !== void 0 ? _a : ownQuery;
    function setQuery(value) {
        setOwnQuery(value);
        onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(value);
    }
    const [page, setPage] = React.useState(1);
    const [results, setResults] = React.useState([]);
    const [hasMore, setHasMore] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const debouncedQuery = useDebounce(query, 700);
    // Only the newest request may write state — switching source unmounts this
    // view outright, and an older page landing late would otherwise overwrite
    // the current query's results.
    const requestId = React.useRef(0);
    const effectiveQuery = (q) => q.trim() || DEFAULT_QUERY;
    const cacheKey = (q) => `${effectiveQuery(q)}::${JSON.stringify(searchParameters || {})}`;
    const { ref: resultsRef, onScroll: onResultsScroll } = useInfiniteScroll({
        loading,
        hasMore: hasMore && !error,
        count: results.length,
        resetKey: cacheKey(debouncedQuery),
        onLoadMore: () => loadMore(),
    });
    React.useEffect(() => () => { requestId.current++; }, []);
    React.useEffect(() => {
        if (!show)
            return;
        const cached = searchCache.get(cacheKey(debouncedQuery));
        if (cached) {
            setResults(cached.results);
            setPage(cached.page);
            setHasMore(cached.hasMore);
            setError(null);
            return;
        }
        setPage(1);
        search(debouncedQuery, 1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery, show]);
    function search(q, pageNumber, append) {
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        context.executeService('Lucy', 'SearchImagesFromUnsplash', Object.assign(Object.assign({}, (searchParameters || {})), { query: effectiveQuery(q), page: pageNumber, per_page: IMAGES_PER_PAGE }), { json: true })
            .then((res) => {
            var _a;
            if (id !== requestId.current)
                return;
            let data = {};
            try {
                data = JSON.parse((_a = res === null || res === void 0 ? void 0 : res[0]) === null || _a === void 0 ? void 0 : _a.data);
            }
            catch (e) { /* tolerate empty */ }
            const photos = ((data === null || data === void 0 ? void 0 : data.results) || (Array.isArray(data) ? data : []));
            const mapped = photos
                .filter(p => { var _a; return ((_a = p === null || p === void 0 ? void 0 : p.urls) === null || _a === void 0 ? void 0 : _a.regular) || (p === null || p === void 0 ? void 0 : p.url); })
                .map(p => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                return ({
                    url: ((_a = p === null || p === void 0 ? void 0 : p.urls) === null || _a === void 0 ? void 0 : _a.regular) || (p === null || p === void 0 ? void 0 : p.url),
                    thumb: ((_b = p === null || p === void 0 ? void 0 : p.urls) === null || _b === void 0 ? void 0 : _b.thumb) || ((_c = p === null || p === void 0 ? void 0 : p.urls) === null || _c === void 0 ? void 0 : _c.small) || ((_d = p === null || p === void 0 ? void 0 : p.urls) === null || _d === void 0 ? void 0 : _d.regular) || (p === null || p === void 0 ? void 0 : p.url),
                    label: (p === null || p === void 0 ? void 0 : p.description) || (p === null || p === void 0 ? void 0 : p.alt_description) || ((_e = p === null || p === void 0 ? void 0 : p.user) === null || _e === void 0 ? void 0 : _e.first_name) || '',
                    meta: { author: (_f = p === null || p === void 0 ? void 0 : p.user) === null || _f === void 0 ? void 0 : _f.name, pageUrl: (_g = p === null || p === void 0 ? void 0 : p.links) === null || _g === void 0 ? void 0 : _g.html, downloadLocation: (_h = p === null || p === void 0 ? void 0 : p.links) === null || _h === void 0 ? void 0 : _h.download_location },
                });
            });
            // search responses carry total_pages; the editorial feed (empty
            // query) is a bare array — assume more while pages come back full
            const more = (data === null || data === void 0 ? void 0 : data.total_pages) ? data.total_pages > pageNumber : photos.length >= IMAGES_PER_PAGE;
            setResults(prev => {
                const next = append ? [...prev, ...mapped] : mapped;
                // cache the accumulated pages for instant repeat loads
                searchCache.set(cacheKey(q), { results: next, page: pageNumber, hasMore: more });
                return next;
            });
            setHasMore(more);
        })
            .catch(() => {
            if (id !== requestId.current)
                return;
            setError(context.$L('uxp-core.media-picker.gallery-error'));
            if (!append)
                setResults([]);
            setHasMore(false);
        })
            .finally(() => {
            if (id !== requestId.current)
                return;
            setLoading(false);
        });
    }
    function loadMore() {
        const next = page + 1;
        setPage(next);
        search(debouncedQuery, next, true);
    }
    // Phones, multiple mode: once a photo here is ticked, a tap ticks (hold starts it)
    const isMobile = useIsMobileLayout();
    const selectMode = isMobile && !!onToggle && results.some(img => !!(isSelected === null || isSelected === void 0 ? void 0 : isSelected(img.url)));
    function renderImage(img) {
        var _a, _b;
        // A card in the grid, or the compact card beside the preview: the photo, its title and who took it
        return _jsx(MediaPickCard, { layout: grid ? 'vertical' : 'horizontal', image: img.thumb, title: img.label || ((_a = img.meta) === null || _a === void 0 ? void 0 : _a.author) || '', description: (_b = img.meta) === null || _b === void 0 ? void 0 : _b.author, selected: !!(isSelected === null || isSelected === void 0 ? void 0 : isSelected(img.url)), onToggle: onToggle ? () => onToggle(img.url) : undefined, active: !!activeValue && activeValue === img.url, onPreview: () => onPick(img.url, img.meta), isMobile: isMobile, selectMode: selectMode }, img.url);
    }
    return _jsxs("div", { className: "uxpcore_mediapicker__list", children: [!hideSearch && _jsx("div", { className: "uxpcore_mediapicker__search", children: _jsx(Input, { value: query, onChange: (v) => setQuery(v), placeholder: context.$L('uxp-core.media-picker.search-gallery'), prefix: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'search'] }), fullWidth: true }) }), _jsxs("div", { className: "uxpcore_mediapicker__results", ref: resultsRef, onScroll: onResultsScroll, children: [error && _jsx("div", { className: "uxpcore_mediapicker__message", children: error }), _jsxs("div", { className: grid ? 'uxpcore_mediapicker__cardgrid' : 'uxpcore_mediapicker__cardlist', children: [results.map(renderImage), loading && Array.from({ length: 8 }, (_, i) => (_jsx("div", { className: "uxpcore_mediapicker__card uxpcore_mediapicker__item--skeleton", children: _jsx(SkeletonLoader, { width: "100%", height: "100%" }) }, `skeleton-${i}`)))] }), !loading && !error && results.length === 0 &&
                        _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L('uxp-core.media-picker.no-images') })] }), _jsx("div", { className: "uxpcore_mediapicker__footer", children: _jsx("a", { href: "https://unsplash.com", target: "_blank", rel: "noreferrer", children: context.$L('uxp-core.media-picker.powered-by-unsplash') }) })] });
};
export default GalleryView;
